/*=== CORE IMPORTS ===*/
import { html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { ViewBase } from '../view-base.js';
import { inboxViewStyles } from './inbox-view-styles.js';

/*=== LIBRARY COMPONENTS ===*/
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/format-date/format-date.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';

/*=== Q1 COMPONENTS ===*/
import '../../components/q1-spinner-panel/q1-spinner-panel.js';

/*=== LIBRARY MODULES ===*/
import orderBy from 'lodash/orderBy';

/*=== Q1 MODULES ===*/
import { enrichVersion } from '../../modules/version-helpers-client.js';
import { buildRefPathMap } from '../../modules/cache-helpers-client.js';

/*=== CONSTANTS ===*/

const _ = { orderBy };

/** The size of the window used to force a server-side cache refresh after multiple reloads.  */
const RECENT_RELOAD_WINDOW = 10; // in seconds


const MEMBER_INBOX_CACHE_ID = (memberId) => `member-inbox:${memberId}`;
const ALL_MEMBERS_CACHE_ID = 'all-members-search';
const ALL_PROJECTS_CACHE_ID = 'all-projects-search';
const ALL_GUILDS_CACHE_ID = 'all-guilds-search';

const INBOX_CACHE_MAX_AGE = 1; // in minutes
const MEMBERS_CACHE_MAX_AGE = 30; // in minutes
const PROJECTS_CACHE_MAX_AGE = 5; // in minutes
const GUILDS_CACHE_MAX_AGE = 5; // in minutes

/*=== VIEW CLASS ===*/

export class InboxView extends ViewBase {
  static styles = [
    ...super.styles,
    inboxViewStyles,
  ];

  static properties = {
    ...super.properties,

    memberInboxCache: { type: Object },
    allMembersCache: { type: Object },
    allProjectsCache: { type: Object },
    allGuildsCache: { type: Object },
    
    proposerVersions: { type: Array },
    approverVersions: { type: Array },
    
    initComplete: { type: Boolean },
    refreshing: { type: Boolean },
    error: { type: Boolean },
    errorMessage: { type: String },
  };

  constructor() {
    super();

    this.memberInboxCache = {};
    this.allMembersCache = {};
    this.allProjectsCache = {};
    this.allGuildsCache = {};
    
    this.proposerVersions = [];
    this.approverVersions = [];

    this.initComplete = false;
    this.refreshing = true;
    this.error = false;
    this.errorMessage = '';
  }

  /*=== LIFECYCLE ===*/

  connectedCallback() {
    super.connectedCallback();

    this.setViewMeta();

    // If the viewer is reloading the page multiple times they probably want the latest data...
    const recentReloadCount = this._trackRecentReloadCount();
    this.refreshInbox(recentReloadCount >= 2);
  }

  /*=== ACTIONS ===*/

  _getCache(cacheId, maxAge, forceRefresh) {
    if (forceRefresh) return this.appElement.cacheManager.refreshLocalCache(cacheId);
    return this.appElement.cacheManager.getCache(cacheId, maxAge);
  }

  async refreshInbox(forceLocalCacheRefresh = true) {
    this.refreshing = true;
    this.error = false;
    this.errorMessage = '';

    try {
      // First get all of the caches and wait for all to resolve
      [
        this.memberInboxCache,
        this.allMembersCache,
        this.allProjectsCache,
        this.allGuildsCache,
      ] = await Promise.all([
        this._getCache(this.memberInboxCacheId, INBOX_CACHE_MAX_AGE, forceLocalCacheRefresh),
        this._getCache(ALL_MEMBERS_CACHE_ID, MEMBERS_CACHE_MAX_AGE, forceLocalCacheRefresh),
        this._getCache(ALL_PROJECTS_CACHE_ID, PROJECTS_CACHE_MAX_AGE, forceLocalCacheRefresh),
        this._getCache(ALL_GUILDS_CACHE_ID, GUILDS_CACHE_MAX_AGE, forceLocalCacheRefresh),
      ]);

      // Rebuild the version lists and mark init complete (which lets the page render)
      this._rebuildVersionLists();
      this.initComplete = true;

      if (
        this.memberInboxCache.isNewOrStale
        || this.allMembersCache.isNewOrStale
        || this.allProjectsCache.isNewOrStale
        || this.allGuildsCache.isNewOrStale
      ) {
        // Now check for new/stale caches and refresh any that need it
        [
          this.memberInboxCache,
          this.allMembersCache,
          this.allProjectsCache,
          this.allGuildsCache,
        ] = await Promise.all([
          this.memberInboxCache.isNewOrStale
            ? this.memberInboxCache.refreshComplete
            : this.memberInboxCache,
          
          this.allMembersCache.isNewOrStale
            ? this.allMembersCache.refreshComplete
            : this.allMembersCache,
          
          this.allProjectsCache.isNewOrStale
            ? this.allProjectsCache.refreshComplete
            : this.allProjectsCache,
          
          this.allGuildsCache.isNewOrStale
            ? this.allGuildsCache.refreshComplete
            : this.allGuildsCache,
        ]);

        // Now rebuild the version lists one more time and we're done
        this._rebuildVersionLists();
      }
    } catch (error) {
      this.error = true;
      this.errorMessage = 
        `Failed to load your inbox. Please try again later. Error: ${error.message}`;
    } finally {
      this.refreshing = false;
      this.initComplete = true;
    }
  }

  openVersion(event) {
    const versionPath = event.currentTarget.dataset.versionPath;

    if (versionPath) {
      if (event.ctrlKey || event.metaKey || event.shiftKey) {
        window.open(versionPath, '_blank');
        return;
      }

      this.appElement.navigateTo(versionPath);
    }
  }

  _rebuildVersionLists() {
    // First build the refPathMap for all of the search caches
    const membersByRef = buildRefPathMap(this.allMembersCache.data, 'members', 'members');
    const projectsByRef = buildRefPathMap(this.allProjectsCache.data, 'projects', 'projects');
    const guildsByRef = buildRefPathMap(this.allGuildsCache.data, 'guilds', 'guilds');
    const refPathMap = {
      ...membersByRef,
      ...projectsByRef,
      ...guildsByRef,
    };

    // Now build the proposer and approver version lists, passing the maps to enrichVersion

    let proposerVersions = Object.values(this.memberInboxCache.data.proposerVersions || {});
    proposerVersions.forEach(version => enrichVersion(version, refPathMap));

    let approverVersions = Object.values(this.memberInboxCache.data.approverVersions || {});
    approverVersions.forEach(version => enrichVersion(version, refPathMap));
    
    // Sort the versions by proposedAt date, descending
    this.proposerVersions = _.orderBy(proposerVersions, ['proposedAt'], ['desc']);
    this.approverVersions = _.orderBy(approverVersions, ['proposedAt'], ['desc']);
  }
  
  /*=== GETTERS ===*/

  get memberInboxCacheId() {
    return MEMBER_INBOX_CACHE_ID(this.currentMember?.id);
  }

  get refreshedAt() {
    return this.memberInboxCache?.refreshedAt;
  }
  
  /*=== TEMPLATES ===*/

  get contentTemplate() {
    return html`
      <header-bar class="with-border">
        <h1>q1 Network Inbox</h1>

        <button-bar>
          ${!this.refreshedAt ? null : html`
            <span class="timestamp">
              Refreshed
              <sl-relative-time sync .date=${this.refreshedAt}></sl-relative-time>
            </span>
          `}

          <sl-button
            id="refresh-button"
            @click=${this.refreshInbox}
            ?disabled=${this.refreshing}
          >
            ${this.refreshing ? html`
              <sl-spinner slot="prefix"></sl-spinner>
              Refreshing
            ` : html`
              <sl-icon slot="prefix" library="material" name="refresh"></sl-icon>
              Refresh
            `}
          </sl-button>
        </button-bar>
      </header-bar>

      <!--- APPROVER VERSIONS --->

      ${this.inboxTableTemplate('Next Actions for You', this.approverVersions, 'approver')}

      <!--- PROPOSER VERSIONS --->

      ${this.inboxTableTemplate('Proposed by You', this.proposerVersions, 'proposer')}

      <q1-spinner-panel label="Loading" ?open=${!this.initComplete}></q1-spinner-panel>
    `;
  }

  /**
   * Renders an inbox table.
   * 
   * @param {*} title - The title of the table
   * @param {*} versions - Array of version objects
   * @param {*} mode - 'proposer' | 'approver'
   */
  inboxTableTemplate(title, versions, mode) {
    const memberTitle = mode === 'proposer' ? 'Approvers' : 'Proposer';
    const memberField = mode === 'proposer' ? 'approvers_members' : 'proposer_member';
    
    return html`
      <h2>${title}</h2>

      <table class="q1">
        <thead>
          <tr>
            <th>Related To</th>
            <th>${memberTitle}</th>
            <th>Item Details</th>
            <th>Proposed At</th>
          </tr>
        </thead>
        <tbody>
          ${versions.length ? '' : html`
            <tr>
              <td colspan="4" class="empty-row">
                <sl-icon library="material" name="block"></sl-icon>
                All done for now!
              </td>
            </tr>
          `}
          ${versions.map(version => html`
            <tr @click=${this.openVersion} data-version-path=${version.detailViewPath}>
              <td class="parent-col">
                <sl-avatar
                  image=${version.parentData?.avatar}
                  shape=${version.parentAvatarShape}
                >
                  <sl-icon
                    slot="icon"
                    library="material"
                    name=${version.subjectCollection_icon}
                  ></sl-icon>
                </sl-avatar>
                <span>${version.parentData?.name}</span>
              </td>
              <td class="member-col">
                <span>
                  ${
                    Array.isArray(version[memberField])
                      ? version[memberField].map(member => member?.name).join(', ')
                      : version[memberField]?.name
                  }
                </span>
              </td>
              <td class="item-col">
                <span>
                  <span class="item-title">
                    ${version.contentData_label ? version.approvalFlow_label + ': ' : ''}
                    ${version.contentData_label}
                  </span>
                  <span class="item-note">
                    ${version.proposerNote_firstParagraph}
                  </span>
                </span>
              </td>
              <td class="date-col">
                <sl-format-date
                  date=${version.proposedAt}
                  month="short" day="numeric" year="numeric"
                ></sl-format-date>
              </td>
            </tr>
          `)}
        </tbody>
      </table>
    `;
  }

  /*=== UTILITY METHODS ===*/

  /**
   * Call this on connectedCallback in order to update the local storage tracker of how many 
   * times the viewer has RELOADED this page in the last RECENT_RELOAD_WINDOW seconds. 
   * 
   * This is used to force a server-side refresh if it seems like the viewer expects it.
   * 
   * @returns {number} The number of times the user has reloaded this page in the last
   * RECENT_RELOAD_WINDOW seconds.
   */
  _trackRecentReloadCount(viewKey = 'inbox') {
    // Generate the current timestamp and calculate when our recent window started
    const currentTimestamp = Date.now();
    const windowStart = currentTimestamp - (RECENT_RELOAD_WINDOW * 1000);
    
    // Get the array of recent timestamps from local storage, add the current timestamp, and filter
    // out any timestamps that are older than the window start
    let reloadTimestamps = [
      ...(JSON.parse(localStorage.getItem(`reloadTimestamps:${viewKey}`)) || []),
      currentTimestamp,
    ].filter(ts => ts > windowStart);

    // Save the updated array back to local storage
    localStorage.setItem(`reloadTimestamps:${viewKey}`, JSON.stringify(reloadTimestamps));

    return reloadTimestamps.length;
  }

}

customElements.define('inbox-view', InboxView);